/* ═══════════════════════════════════════════════════════════════
   REUNITED — Homepage scroll controller
   Port of ReunitedScroll.tsx scene/video logic to vanilla JS.
   One snap = one scene = one auto-played video segment.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var wrap = document.getElementById('reunited-scroll');
  if (!wrap) return;
  var video = document.getElementById('ru-video');
  var media = document.getElementById('ru-media');
  var leak = document.getElementById('ru-light-leak');
  var floatBtn = document.getElementById('ru-float');
  var dots = Array.prototype.slice.call(wrap.querySelectorAll('[data-dot]'));
  var sceneWraps = Array.prototype.slice.call(wrap.querySelectorAll('.ru-scene-wrap'));
  var SCENE_COUNT = 7;

  // Exact BREAKS from the Lovable source
  var BREAKS = [0, 0.10, 0.19, 0.39, 0.58, 0.69, 0.82, 1.0];

  // mediaSlot atmospheres + opacities (exact)
  var ATMOS = [
    'radial-gradient(ellipse 36% 36% at 50% 62%, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.008) 18%, transparent 58%)',
    'radial-gradient(ellipse 30% 36% at 50% 44%, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 18%, transparent 60%)',
    'radial-gradient(ellipse 32% 38% at 50% 48%, rgba(255,255,255,0.028) 0%, rgba(255,255,255,0.008) 18%, transparent 60%)',
    'radial-gradient(ellipse 30% 36% at 54% 38%, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.008) 18%, transparent 60%)',
    'radial-gradient(ellipse 12% 34% at 50% 8%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.02) 16%, transparent 40%)',
    'radial-gradient(ellipse 26% 30% at 50% 52%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.006) 16%, transparent 52%)',
    'radial-gradient(ellipse 30% 28% at 50% 54%, rgba(255,255,255,0.026) 0%, rgba(255,255,255,0.008) 16%, transparent 56%)'
  ];
  var ATMOS_OPACITY = [0.14, 0.16, 0.12, 0.12, 0.2, 0.08, 0.1];

  var isMob = function () { return window.innerWidth < 640; };

  /* ── Always open at the top; don't let the browser restore a scroll position ── */
  try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}
  try { wrap.scrollTop = 0; } catch (e) {}

  /* ── Intro reveal gating: hold the branded intro until the brand has shown
        AND scene 0 is settled on the video, so it lifts straight to the hero,
        with a hard cap so it can never hang. ── */
  var introRevealed = false;
  var brandMinElapsed = false;
  var sceneZeroReady = false;
  function doReveal() {
    if (introRevealed) return;
    introRevealed = true;
    try { wrap.scrollTop = 0; } catch (e) {}
    activateSection(0);
    wrap.classList.add('is-revealed');
  }
  function tryReveal() { if (brandMinElapsed && sceneZeroReady) doReveal(); }
  setTimeout(function () { brandMinElapsed = true; tryReveal(); }, 1400);
  setTimeout(doReveal, 3500);

  /* ── Video readiness ── */
  function revealVideo() { if (video) video.classList.add('is-ready'); }
  var fallbackReady = setTimeout(revealVideo, 1200);
  if (video) {
    var onReady = function () {
      if (video.readyState >= 1 || isFinite(video.duration)) revealVideo();
    };
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('error', revealVideo, { once: true });
    // Park on the first frame; the scene-0 segment (below) drives playback.
    // No uncontrolled play here, that caused a flash of the wrong frame.
    var seekStart = function () { try { video.currentTime = 0; } catch (e) {} };
    video.addEventListener('loadedmetadata', seekStart, { once: true });
    try { video.load(); } catch (e) {}
  }
  if (!video) { sceneZeroReady = true; tryReveal(); }

  /* ── Gesture unlock (Chrome blocks programmatic play/seek) ── */
  var gestureUnlocked = false;
  function unlock() {
    if (gestureUnlocked || !video) return;
    gestureUnlocked = true;
    try {
      var p = video.play();
      if (p && p.then) p.then(function () { try { video.pause(); } catch (e) {} }).catch(function () {});
    } catch (e) {}
  }
  ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (ev) {
    window.addEventListener(ev, unlock, { once: true, passive: true });
  });

  /* ── Video segment playback ── */
  var currentSection = -1;
  var rafId = null;
  var endTime = 0;
  var pendingSegment = null;
  var stallTimer = null;

  function clearStallTimer() { if (stallTimer != null) { clearTimeout(stallTimer); stallTimer = null; } }

  function forceSegmentEnd() {
    if (!video) return;
    try { video.currentTime = endTime; } catch (e) {}
    try { video.pause(); } catch (e) {}
    clearStallTimer();
    if (currentSection === 0 && !sceneZeroReady) { sceneZeroReady = true; tryReveal(); }
  }

  function watchEnd() {
    rafId = null;
    if (!video) return;
    if (video.currentTime >= endTime - 0.04) {
      video.pause();
      try { video.currentTime = endTime; } catch (e) {}
      if (video.playbackRate !== 1) video.playbackRate = 1;
      clearStallTimer();
      if (currentSection === 0 && !sceneZeroReady) { sceneZeroReady = true; tryReveal(); }
      return;
    }
    rafId = requestAnimationFrame(watchEnd);
  }

  function playSegment(index, prevIndex) {
    if (!video) return;
    if (!video.duration || !isFinite(video.duration)) { pendingSegment = { index: index, prevIndex: prevIndex }; return; }
    pendingSegment = null;
    var startFrac = BREAKS[index] != null ? BREAKS[index] : 0;
    var endFrac = BREAKS[index + 1] != null ? BREAKS[index + 1] : 1;
    var startT = startFrac * video.duration;
    endTime = Math.min(video.duration - 0.001, endFrac * video.duration);

    var jump = prevIndex == null ? 0 : index - prevIndex;

    if (jump === 1) {
      if (video.currentTime < startT - 0.25 || video.currentTime > endTime) {
        try { video.currentTime = startT; } catch (e) {}
      }
      video.playbackRate = 1;
    } else if (jump > 1) {
      try { video.currentTime = startT; } catch (e) {}
      video.playbackRate = Math.min(2.5, 1 + (jump - 1) * 0.6);
    } else {
      try { video.currentTime = startT; } catch (e) {}
      video.playbackRate = 1;
    }

    var playRejected = false;
    try {
      var pp = video.play();
      if (pp && pp.then) { pp.catch(function () { playRejected = true; forceSegmentEnd(); }); }
    } catch (e) { playRejected = true; forceSegmentEnd(); }

    if (rafId != null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(watchEnd);

    clearStallTimer();
    var startedAt = video.currentTime;
    stallTimer = setTimeout(function () {
      if (!video) return;
      if (playRejected) return;
      if (Math.abs(video.currentTime - startedAt) < 0.05 && video.currentTime < endTime - 0.04) {
        forceSegmentEnd();
      }
    }, 1200);
  }

  if (video) {
    video.addEventListener('loadedmetadata', function () {
      if (pendingSegment) playSegment(pendingSegment.index, pendingSegment.prevIndex);
    });
  }

  /* ── Header stays transparent across the whole homepage (matches Lovable) ── */
  function syncHeader(idx) {
    var header = document.getElementById('ru-header');
    if (!header) return;
    header.classList.add('is-transparent');
  }

  /* ── Restart entry animations for a freshly-shown scene ── */
  function retriggerAnimations(el) {
    var animated = el.querySelectorAll('.gold-shine, .ru-hero-inner, .ru-s02-detail, .ru-benefit, .ru-active, .ru-pills, .ru-testi, .ru-badges');
    animated.forEach(function (node) {
      var prev = node.style.animation;
      node.style.animation = 'none';
      // force reflow
      void node.offsetWidth;
      node.style.animation = prev || '';
    });
  }

  /* ── Activate a scene (the fix: only one scene visible at a time) ── */
  function activateSection(index) {
    var safe = Math.max(0, Math.min(SCENE_COUNT - 1, index));
    if (safe === currentSection) return;
    var prev = currentSection;
    currentSection = safe;

    // Toggle scene visibility — replicates scIdx === N conditional mount
    sceneWraps.forEach(function (w) {
      var s = parseInt(w.getAttribute('data-scene'), 10);
      if (s === safe) {
        w.classList.add('is-active');
        retriggerAnimations(w);
      } else {
        w.classList.remove('is-active');
      }
    });

    // Container scene marker (drives scroll-hint + any [data-scene] CSS)
    wrap.setAttribute('data-scene', String(safe));

    // Nav dots
    dots.forEach(function (d, i) { d.classList.toggle('is-active', i === safe); });

    // Atmosphere + light leak
    if (media) {
      media.style.background = ATMOS[safe] || 'none';
      media.style.opacity = ATMOS_OPACITY[safe] != null ? ATMOS_OPACITY[safe] : 0.08;
    }
    if (leak) {
      if (safe === 4) {
        leak.style.opacity = '0.04';
        leak.style.background = 'radial-gradient(ellipse 18% 52% at 50% 10%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.03) 24%, transparent 58%)';
      } else if (safe === 6) {
        leak.style.opacity = '0.02';
        leak.style.background = 'radial-gradient(ellipse 30% 36% at 50% 46%, rgba(255,255,255,0.05) 0%, transparent 100%)';
      } else {
        leak.style.opacity = '0.01';
        leak.style.background = 'radial-gradient(ellipse 30% 36% at 50% 46%, rgba(255,255,255,0.05) 0%, transparent 100%)';
      }
    }

    // Floating Shop Now — desktop, scenes 1–5
    if (floatBtn) {
      var show = !isMob() && safe >= 1 && safe <= 5;
      floatBtn.classList.toggle('is-visible', show);
    }

    syncHeader(safe);
    playSegment(safe, prev < 0 ? null : prev);
  }

  /* ── IntersectionObserver on the 7 snap sections ── */
  var io = new IntersectionObserver(function (entries) {
    var best = null;
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (!e.isIntersecting) continue;
      if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
    }
    if (!best) return;
    activateSection(parseInt(best.target.getAttribute('data-scene') || '0', 10));
  }, { root: wrap, threshold: [0.6] });

  wrap.querySelectorAll('.reunited-snap-section[data-scene]').forEach(function (s) { io.observe(s); });
  activateSection(0);

  /* ── Shop Now buttons → leave overlay then navigate ── */
  var shopUrl = wrap.getAttribute('data-shop-url') || '/collections/all';
  var leaving = false;
  wrap.querySelectorAll('[data-shop]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (leaving) return;
      leaving = true;
      wrap.classList.add('is-leaving');
      setTimeout(function () { window.location.href = shopUrl; }, 560);
    });
  });

  window.addEventListener('resize', function () {
    if (floatBtn) {
      var show = !isMob() && currentSection >= 1 && currentSection <= 5;
      floatBtn.classList.toggle('is-visible', show);
    }
  });
})();